(function () {
  'use strict';

  var SECTION_SELECTOR = '.oddit-section-ugc-carousel';
  var SCROLL_SETTLE_FALLBACK_MS = 800;
  var VIDEO_PREVIEW_LIMIT_S = 5;
  var sectionState = new Map();

  // ---------------------------------------------------------------------------
  // UGC video: hover plays a muted, controls-free 5-second preview; click
  // promotes the video to full playback with native controls; on `ended` the
  // video is reset (poster restored via load()) so the next hover starts fresh.
  // ---------------------------------------------------------------------------
  function initVideo(videoEl) {
    if (videoEl.dataset.ugcVideoInitialized === 'true') return;
    videoEl.dataset.ugcVideoInitialized = 'true';

    var previewMode = true;

    function safePlay() {
      var p = videoEl.play();
      if (p && typeof p.catch === 'function') p.catch(function () {});
    }

    function rewind() {
      try { videoEl.currentTime = 0; } catch (e) {}
    }

    function startPreview() {
      if (!previewMode) return;
      videoEl.muted = true;
      videoEl.controls = false;
      rewind();
      safePlay();
    }

    function endPreview() {
      if (!previewMode) return;
      videoEl.pause();
      rewind();
    }

    function onTimeUpdate() {
      if (!previewMode) return;
      // Loop the first N seconds while hovering: rewind without pausing so
      // playback continues seamlessly from 0. Some browsers briefly pause
      // on a programmatic seek, so re-issue play() if needed.
      if (videoEl.currentTime >= VIDEO_PREVIEW_LIMIT_S) {
        rewind();
        if (videoEl.paused) safePlay();
      }
    }

    function onClick() {
      // After promotion to full-play, native controls handle subsequent clicks.
      if (!previewMode) return;
      previewMode = false;
      videoEl.muted = false;
      videoEl.controls = true;
      rewind();
      safePlay();
    }

    function onEnded() {
      previewMode = true;
      videoEl.controls = false;
      videoEl.muted = true;
      // Reload so the poster image re-renders instead of leaving the last frame.
      try { videoEl.load(); } catch (e) {}
    }

    videoEl.addEventListener('mouseenter', startPreview);
    videoEl.addEventListener('mouseleave', endPreview);
    videoEl.addEventListener('click', onClick);
    videoEl.addEventListener('timeupdate', onTimeUpdate);
    videoEl.addEventListener('ended', onEnded);
  }

  function initVideos(section) {
    var videos = section.querySelectorAll('[data-ugc-video]');
    Array.prototype.forEach.call(videos, initVideo);
  }

  function initSection(section) {
    if (!section || section.dataset.odditInitialized === 'true') return;
    section.dataset.odditInitialized = 'true';

    var track = section.querySelector('[data-ugc-track]');
    var prevBtn = section.querySelector('[data-ugc-prev]');
    var nextBtn = section.querySelector('[data-ugc-next]');
    var indicatorsList = section.querySelector('[data-ugc-indicators]');

    if (!track) return;

    var state = {
      dots: [],
      cardsPerView: 1,
      pageCount: 1,
      currentPage: 0,
      programmaticScroll: false,
      programmaticTimer: null,
      scrollendHandler: null,
      resizeObserver: null
    };

    function getCards() {
      return Array.prototype.slice.call(track.children);
    }

    // Center-to-center distance between the first two cards (card width + gap).
    function getStepWidth() {
      var cards = getCards();
      if (cards.length < 2) {
        return cards[0] ? cards[0].getBoundingClientRect().width : 0;
      }
      var firstRect = cards[0].getBoundingClientRect();
      var secondRect = cards[1].getBoundingClientRect();
      return secondRect.left - firstRect.left;
    }

    // How many whole cards fit in the visible track at the current breakpoint.
    function getCardsPerView() {
      var step = getStepWidth();
      if (step <= 0) return 1;
      return Math.max(1, Math.round(track.clientWidth / step));
    }

    function getCurrentCardIndex() {
      var step = getStepWidth();
      if (step <= 0) return 0;
      return Math.round(track.scrollLeft / step);
    }

    function getCurrentPageFromScroll() {
      var cpv = state.cardsPerView;
      if (cpv <= 0) return 0;
      var page = Math.round(getCurrentCardIndex() / cpv);
      return Math.max(0, Math.min(page, state.pageCount - 1));
    }

    // Clear the programmatic flag once the scroll actually settles, so
    // mid-animation scroll events don't reset the dot to an intermediate page.
    function armScrollSettle() {
      if (state.programmaticTimer) {
        clearTimeout(state.programmaticTimer);
        state.programmaticTimer = null;
      }
      if (state.scrollendHandler) {
        track.removeEventListener('scrollend', state.scrollendHandler);
        state.scrollendHandler = null;
      }

      var release = function () {
        state.programmaticScroll = false;
        if (state.scrollendHandler) {
          track.removeEventListener('scrollend', state.scrollendHandler);
          state.scrollendHandler = null;
        }
        if (state.programmaticTimer) {
          clearTimeout(state.programmaticTimer);
          state.programmaticTimer = null;
        }
      };

      // Prefer the native scrollend event (Chrome 114+, Firefox 109+, Safari 17.4+).
      state.scrollendHandler = release;
      track.addEventListener('scrollend', release, { once: true, passive: true });

      // Fallback for browsers without scrollend, or in case the event never fires
      // (e.g. when scrollTo is clamped and no movement actually happens).
      state.programmaticTimer = setTimeout(release, SCROLL_SETTLE_FALLBACK_MS);
    }

    function scrollToPage(page) {
      if (page < 0) page = 0;
      if (page > state.pageCount - 1) page = state.pageCount - 1;
      state.currentPage = page;

      var step = getStepWidth();
      var maxScroll = track.scrollWidth - track.clientWidth;
      var target = Math.min(step * state.cardsPerView * page, maxScroll);

      state.programmaticScroll = true;
      track.scrollTo({ left: target, behavior: 'smooth' });
      armScrollSettle();

      updateActiveDot(page);
      updateButtonStates(page);
    }

    function updateActiveDot(activePage) {
      state.dots.forEach(function (dot, i) {
        var isActive = i === activePage;
        dot.classList.toggle('oddit-section-ugc-carousel__indicator--active', isActive);
        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    function updateButtonStates(activePage) {
      var isCarousel = state.pageCount > 1;
      if (prevBtn) {
        prevBtn.hidden = !isCarousel;
        prevBtn.disabled = activePage <= 0;
      }
      if (nextBtn) {
        nextBtn.hidden = !isCarousel;
        nextBtn.disabled = activePage >= state.pageCount - 1;
      }
    }

    // (Re)build the pagination dots for the current page count.
    function buildIndicators() {
      if (!indicatorsList) return;
      indicatorsList.innerHTML = '';
      state.dots = [];

      if (state.pageCount <= 1) {
        // Not a carousel: a single static dot signals it *could* scroll.
        var li = document.createElement('li');
        var span = document.createElement('span');
        span.className =
          'oddit-section-ugc-carousel__indicator ' +
          'oddit-section-ugc-carousel__indicator--active ' +
          'oddit-section-ugc-carousel__indicator--static';
        span.setAttribute('aria-hidden', 'true');
        li.appendChild(span);
        indicatorsList.appendChild(li);
        return;
      }

      for (var p = 0; p < state.pageCount; p++) {
        var item = document.createElement('li');
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'oddit-section-ugc-carousel__indicator';
        btn.setAttribute('data-ugc-go', String(p));
        btn.setAttribute('aria-label', 'Go to page ' + (p + 1));
        var inner = document.createElement('span');
        inner.setAttribute('aria-hidden', 'true');
        inner.innerHTML = '&zwnj;';
        btn.appendChild(inner);
        (function (pageIndex) {
          btn.addEventListener('click', function () {
            scrollToPage(pageIndex);
          });
        })(p);
        item.appendChild(btn);
        indicatorsList.appendChild(item);
        state.dots.push(btn);
      }
    }

    // Recompute layout-dependent values; rebuild dots only when they change.
    function recompute() {
      var cards = getCards();
      var cpv = getCardsPerView();
      var pages = Math.max(1, Math.ceil(cards.length / cpv));
      var canScroll = track.scrollWidth - track.clientWidth > 1;
      if (!canScroll) {
        pages = 1;
      } else if (pages < 2) {
        pages = 2;
      }
      var changed = cpv !== state.cardsPerView || pages !== state.pageCount;
      state.cardsPerView = cpv;
      state.pageCount = pages;
      if (changed) buildIndicators();
      // Re-derive current page from scrollLeft after a layout change.
      var page = getCurrentPageFromScroll();
      state.currentPage = page;
      updateActiveDot(page);
      updateButtonStates(page);
    }

    function syncFromScroll() {
      var page = getCurrentPageFromScroll();
      state.currentPage = page;
      updateActiveDot(page);
      updateButtonStates(page);
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        // Use the tracked currentPage, not a read of mid-animation scrollLeft.
        scrollToPage(state.currentPage - 1);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        scrollToPage(state.currentPage + 1);
      });
    }

    track.addEventListener('scroll', function () {
      // While we are driving the scroll programmatically, ignore intermediate
      // scroll events — they would briefly reset the dot to a wrong page.
      if (state.programmaticScroll) return;
      syncFromScroll();
    }, { passive: true });

    if (typeof ResizeObserver === 'function') {
      state.resizeObserver = new ResizeObserver(function () {
        recompute();
      });
      state.resizeObserver.observe(track);
    }

    recompute();
    initVideos(section);
    sectionState.set(section.id || section, state);
  }

  function destroySection(section) {
    var state = sectionState.get(section.id || section);
    if (!state) return;
    if (state.programmaticTimer) clearTimeout(state.programmaticTimer);
    if (state.resizeObserver) state.resizeObserver.disconnect();
    sectionState.delete(section.id || section);
    section.dataset.odditInitialized = '';
  }

  function init() {
    document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) destroySection(section);
  });
})();
